import { Box, IconButton, useDisclosure } from '@chakra-ui/react';
import React, { useCallback, useState, useRef } from 'react';

import {
  date as commonDate,
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Bank, Funding } from '@finley-technologies/finley-types';

import { DrawerType, Drawer } from './Drawer';
import { CheckAvailabilityRow } from './Row';
import {
  FundingRequestTypeEnumGQLType,
  ResolvedLoanFragmentGQLType,
} from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  formatCurrencyAmount,
  numberToPercentageLabel,
} from '~/packages/common-frontend/utils';
import { useFundingRequestTemplateContextV1 } from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';
import { LoanDetailsSideDrawer } from '~/packages/invoices/ui/create/LoanDetailsSideDrawer';

type CheckAvailabilityProps = { type: FundingRequestTypeEnumGQLType };

export const CheckAvailability = ({ type }: CheckAvailabilityProps) => {
  // @VLYSLS @jendevelops conditionally hide borrowing base dependent values if no cfs w/ borrowing base
  const { isSlsAgentLender } = useFeatureFlags();

  const { context, template } = useFundingRequestTemplateContextV1();

  const {
    summary: {
      commitment,
      maximumAvailableAmount,
      facilityUtilization,
      borrowingBaseAmount,
      outstandingBalance,
      borrowingBaseReportMetadata,
      assignedEligibleExposureSinceLastApprovedFundingRequest,
      borrowingBaseReportMetadataForLastApprovedFundingRequest,
    },
    data: { recycleRequests },
  } = context;

  // This resolved loan reference is used for upsizing request checks
  let resolvedLoan: ResolvedLoanFragmentGQLType | null = null;
  if (type === 'upsizing') {
    finleyAssert(
      template && template.loans && template.loans.length === 1,
      'Request must contain 1 loan for upsizing',
    );
    resolvedLoan = template.loans[0];
  }

  const hasRecycleRequests = recycleRequests.length > 0;

  const borrowingBaseBtnRef = useRef<HTMLButtonElement>(null);
  const pendingDrawRequestsBtnRef = useRef<HTMLButtonElement>(null);
  const pendingRecycleRequestsBtnRef = useRef<HTMLButtonElement>(null);

  const [drawerType, setDrawerType] = useState<DrawerType | null>(null);

  const onDrawerOpen = useCallback(
    (_drawerType: DrawerType) => {
      setDrawerType(_drawerType);
    },
    [setDrawerType],
  );

  const onDrawerClose = useCallback(() => {
    setDrawerType(null);
  }, [setDrawerType]);

  const getFocusBtnRef = (_drawerType: DrawerType | null) => {
    switch (_drawerType) {
      case 'borrowingBase':
        return borrowingBaseBtnRef;
      case 'pendingDrawRequests':
        return pendingDrawRequestsBtnRef;
      case 'pendingRecycleRequests':
        return pendingRecycleRequestsBtnRef;
      default:
        return undefined;
    }
  };

  const pendingDrawRequestAmount =
    context.allowedActions === 'mutable'
      ? context.summary.pendingRequests
          .filter((request) => request.type === 'drawRequest')
          .reduce<number>((acc, request) => acc + request.amount, 0)
      : undefined;

  const pendingRecycleRequestsAmount =
    context.allowedActions === 'mutable'
      ? context.summary.pendingRequests
          .filter((request) => request.type === 'collectionsRecycle')
          .reduce<number>((acc, request) => acc + request.amount, 0)
      : undefined;

  const borrowingBaseReportMetadataString = borrowingBaseReportMetadata
    ? ` as of ${commonDate.fullDateStrToHumanDateString(
        borrowingBaseReportMetadata.reportingDate,
      )}, Version ${borrowingBaseReportMetadata.fileVersion}`
    : undefined;

  const newlyAssignedEligibleExposureDateString =
    borrowingBaseReportMetadata &&
    borrowingBaseReportMetadataForLastApprovedFundingRequest
      ? `from ${commonDate.fullDateStrToHumanDateString(
          borrowingBaseReportMetadataForLastApprovedFundingRequest.reportingDate,
        )} - ${commonDate.fullDateStrToHumanDateString(
          borrowingBaseReportMetadata.reportingDate,
        )}`
      : '';

  const {
    isOpen: isLoanSideDrawerOpen,
    onOpen: onLoanSideDrawerOpen,
    onClose: onLoanSideDrawerClose,
  } = useDisclosure();

  const fundingMechanics: { loanMechanics: Funding.LoanMechanics } = JSON.parse(
    template?.fundingMechanicsV2Serialized ?? '{}',
  );
  const isTermLoan = Boolean(fundingMechanics.loanMechanics?.isTermLoan);

  return (
    <>
      <Box w="100%">
        <CheckAvailabilityRow
          rowIcon="circle-dollar"
          value={
            formatCurrencyAmount(commitment.amount, Bank.Currency.USD) || '-'
          }
          label={
            isTermLoan ? 'Adjusted Commitment Amount' : 'Commitment Amount'
          }
        />

        {/* @VLYSLS @jendevelops conditionally hide borrowing base dependent values if no cfs w/ borrowing base */}
        {Boolean(borrowingBaseAmount) && !isSlsAgentLender && (
          <CheckAvailabilityRow
            rowIcon="circle-dollar"
            value={
              formatCurrencyAmount(borrowingBaseAmount, Bank.Currency.USD) ||
              '-'
            }
            label={`Total Borrowing Base${borrowingBaseReportMetadataString}`}
            actionComponent={
              <IconButton
                icon={<Icon icon="chevron-right" />}
                variant="ghost"
                colorScheme="v2-aurora"
                aria-label="View Borrowing Base details"
                ref={borrowingBaseBtnRef}
                onClick={() => onDrawerOpen('borrowingBase')}
              />
            }
          />
        )}

        {/* // @VLYSLS @jendevelops conditionally hide borrowing base dependent values if no cfs w/ borrowing base */}
        {Boolean(borrowingBaseAmount) && !isSlsAgentLender && (
          <CheckAvailabilityRow
            rowIcon="circle-dollar"
            value={
              formatCurrencyAmount(maximumAvailableAmount, Bank.Currency.USD) ||
              '-'
            }
            label="Maximum Available Amount"
            // @jendevelops TODO: add tooltip
          />
        )}
        <CheckAvailabilityRow
          rowIcon="circle-dollar"
          value={
            formatCurrencyAmount(outstandingBalance, Bank.Currency.USD) || '-'
          }
          label="Principal"
        />
        {type === 'upsizing' && !!resolvedLoan ? (
          <>
            <CheckAvailabilityRow
              clickRow={onLoanSideDrawerOpen}
              value={
                typeof resolvedLoan.funding.totalOutstandingBalance === 'number'
                  ? formatCurrencyAmount(
                      resolvedLoan.funding.totalOutstandingBalance,
                      Bank.Currency.USD,
                    ) || '-'
                  : '-'
              }
              label={`For Loan ${resolvedLoan.id} ${
                resolvedLoan.externalLoanId
                  ? `(${resolvedLoan.externalLoanId})`
                  : ''
              }`}
              actionComponent={
                <IconButton
                  aria-label="Open loan details side drawer"
                  colorScheme="v2-aurora"
                  icon={<Icon icon="chevron-right" />}
                  onClick={onLoanSideDrawerOpen}
                  variant="ghost"
                />
              }
            />
            <LoanDetailsSideDrawer
              loan={resolvedLoan}
              isOpen={isLoanSideDrawerOpen}
              onClose={onLoanSideDrawerClose}
            />
          </>
        ) : null}
        {!isSlsAgentLender &&
          assignedEligibleExposureSinceLastApprovedFundingRequest !== null && (
            <CheckAvailabilityRow
              rowIcon="circle-dollar"
              value={
                formatCurrencyAmount(
                  commonUtils.jsRound({
                    value: assignedEligibleExposureSinceLastApprovedFundingRequest,
                    precision: 2,
                    mode: 'floor',
                  }),
                  Bank.Currency.USD,
                ) || '-'
              }
              label={`Newly Assigned Eligible Exposure ${newlyAssignedEligibleExposureDateString}`}
            />
          )}
        <CheckAvailabilityRow
          rowIcon="percentage"
          value={numberToPercentageLabel(facilityUtilization, 2) || '-'}
          label="Utilization"
        />
        {typeof pendingDrawRequestAmount === 'number' && (
          <CheckAvailabilityRow
            rowIcon="circle-dollar"
            value={
              formatCurrencyAmount(
                pendingDrawRequestAmount,
                Bank.Currency.USD,
              ) || '-'
            }
            label="Pending Draw Requests"
            clickRow={() => onDrawerOpen('pendingDrawRequests')}
            actionComponent={
              <IconButton
                icon={<Icon icon="chevron-right" />}
                variant="ghost"
                colorScheme="v2-aurora"
                aria-label="View pending draw request details"
                ref={pendingRecycleRequestsBtnRef}
                onClick={() => onDrawerOpen('pendingDrawRequests')}
              />
            }
          />
        )}
        {/* @jendevelops TODO: remove this upsizing check if we make sure that we don't have recycles for upsizing type request */}
        {hasRecycleRequests &&
          type !== 'upsizing' &&
          typeof pendingRecycleRequestsAmount === 'number' && (
            <CheckAvailabilityRow
              rowIcon="circle-dollar"
              value={
                formatCurrencyAmount(
                  pendingRecycleRequestsAmount,
                  Bank.Currency.USD,
                ) || '-'
              }
              label="Pending Recycle Requests"
              actionComponent={
                <IconButton
                  icon={<Icon icon="chevron-right" />}
                  variant="ghost"
                  colorScheme="v2-aurora"
                  aria-label="View pending recycle request details"
                  ref={pendingDrawRequestsBtnRef}
                  onClick={() => onDrawerOpen('pendingRecycleRequests')}
                />
              }
            />
          )}
        {context.allowedActions === 'mutable' && (
          <>
            <CheckAvailabilityRow
              rowIcon="circle-dollar"
              value={
                formatCurrencyAmount(
                  context.summary.drawAvailability,
                  Bank.Currency.USD,
                ) || '-'
              }
              label="Draw Availability"
              // @jendevelops TODO: add tooltip
            />
            {/* @jendevelops TODO: remove this upsizing check if we make sure that we don't have recycles for upsizing type request */}
            {hasRecycleRequests && type !== 'upsizing' && (
              <CheckAvailabilityRow
                rowIcon="circle-dollar"
                value={
                  formatCurrencyAmount(
                    context.summary.recycleAvailability,
                    Bank.Currency.USD,
                  ) || '-'
                }
                label="Recycle Availability"
                // @jendevelops TODO: add tooltip
              />
            )}
          </>
        )}
      </Box>

      <Drawer
        isOpen={Boolean(drawerType)}
        onClose={onDrawerClose}
        finalFocusRef={getFocusBtnRef(drawerType)}
        drawerType={drawerType}
        subTitle={
          drawerType === 'borrowingBase'
            ? borrowingBaseReportMetadataString
            : undefined
        }
      />
    </>
  );
};
